import './index.scss';

import React, {memo, useCallback, useMemo, useState, MouseEvent} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {useSelector} from 'react-redux';
import {HomepageItemModel, HomepageItemType} from '../../../../store/control/state';
import iconUpArrow from '../../../../assets/images/icons/scenario/up-arrow.png';
import iconDownArrow from '../../../../assets/images/icons/scenario/down-arrow.png';
import {useRPC} from '../../../../hooks/rpc';
import {useSetSelector} from '../../../../hooks/set-selector';
import {onClickHandler} from '../../../../utils/onclick';
import {useLocalization} from "../../../../hooks/localization";

export const TYPE_NAME_MAP: Record<HomepageItemType, string> = {
    control: 'CONTROL',
    scenario: 'SCENARIO',
    sensor: 'SENSOR',
    media: 'MEDIA',
};

const keyExtractor = (item: HomepageItemModel) => item.id;

type UpDownProps = {
    index: number;
    onUpClick?: (index: number) => void;
    onDownClick?: (index: number) => void;
};

const UpDown = memo(function UpDown({onUpClick: onUpClickProp, onDownClick: onDownClickProp, index}: UpDownProps) {
    const onUpClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onUpClickProp(index);
    }, [onUpClickProp, index]);

    const onDownClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onDownClickProp(index);
    }, [onDownClickProp, index]);

    return (
        <div className='up-down-button-container'>
            {
                renderIf(onUpClickProp)(() =>
                        <div className='up-down-button' {...onClickHandler(onUpClick)}>
                            <img src={iconUpArrow} alt=''/>
                        </div>,
                    () => <div className='up-down-button'/>,
                )
            }
            {
                renderIf(onDownClickProp)(() =>
                    <div className='up-down-button' {...onClickHandler(onDownClick)}>
                        <img src={iconDownArrow} alt=''/>
                    </div>,
                )
            }
        </div>
    );
});

export const HomepageListScreen = memo(function HomepageListScreen() {
    const {t} = useLocalization();
    const [rpc] = useRPC();

    const homepageItemModelList = useSelector(state => state.control.homepageItemList);
    const homepageItemModelListSorted = useMemo(() =>
            homepageItemModelList.sort((a, b) => a.order - b.order),
        [homepageItemModelList]);
    const controlSet = useSetSelector(state => state.control.controlList);
    const scenarioSet = useSetSelector(state => state.control.scenarioList);
    const inputSet = useSetSelector(state => state.control.inputList);
    const mediaSet = useSetSelector(state => state.control.mediaList);

    const [selectedHomepageItem, setSelectedHomepageItem] = useState<HomepageItemModel>(null);

    const buttons = useMemo(() => [
        {
            text: t('edit'),
            url: `/settings/homepage/edit/${selectedHomepageItem ? selectedHomepageItem.id : ''}`,
            className: 'orange',
            hidden: !(selectedHomepageItem),
        },
        {
            text: t('add'),
            url: '/settings/homepage/edit',
            className: 'green',
        },
    ], [t, selectedHomepageItem]);

    const swapItemOrders = useCallback((itemA: HomepageItemModel, itemB: HomepageItemModel) => {
        Promise.all([
            rpc.updateHomepageItem({...itemA, order: itemB.order}),
            rpc.updateHomepageItem({...itemB, order: itemA.order}),
        ]).then(() => setSelectedHomepageItem(null));
    }, [rpc]);

    const onUpClick = useCallback((index: number) => {
        swapItemOrders(homepageItemModelList[index - 1], homepageItemModelList[index]);
    }, [homepageItemModelList, swapItemOrders]);

    const onDownClick = useCallback((index: number) => {
        swapItemOrders(homepageItemModelList[index + 1], homepageItemModelList[index]);
    }, [homepageItemModelList, swapItemOrders]);

    const displayNameExtractor = useCallback((item: HomepageItemModel, column: ColumnModel, index?: number) => {
        if (column.key === 'name') {
            switch (item.type) {
                case 'control':
                    return controlSet[item.relational_id]?.name || '-';
                case 'sensor':
                    return inputSet[item.relational_id]?.name || '-';
                case 'scenario':
                    return scenarioSet[item.relational_id]?.name || '-';
                case 'media':
                    return mediaSet[item.relational_id]?.name || '-';
                default:
                    return '-';
            }
        } else if (column.key === 'type') {
            return t(TYPE_NAME_MAP[item.type]) || '-';
        } else if (column.key === 'order') {
            return <UpDown
                index={index}
                onUpClick={index === 0 ? undefined : onUpClick}
                onDownClick={(index === homepageItemModelListSorted.length - 1) ? undefined : onDownClick}/>;
        }

        return item[column.key] || '-';
    }, [t, controlSet, inputSet, scenarioSet, mediaSet, onUpClick, homepageItemModelListSorted.length, onDownClick]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('name'),
            key: 'name',
        },
        {
            length: 2,
            name: t('type'),
            key: 'type',
        },
        {
            length: 3,
            name: t('order'),
            key: 'order',
        },
    ], [t]);

    return (
        <>
            <PasswordContainer requiredRole="admin" isFullscreen={true}>
                <SettingsPageContainer
                    buttons={buttons}
                    backUrl={'/settings'}>
                    <SettingsList
                        sortDisabled={true}
                        columns={columns}
                        items={homepageItemModelListSorted}
                        displayNameExtractor={displayNameExtractor}
                        keyExtractor={keyExtractor}
                        selectedItem={selectedHomepageItem}
                        onItemSelected={setSelectedHomepageItem}/>
                </SettingsPageContainer>
            </PasswordContainer>
        </>
    );
});
